import React, { useState, useEffect } from "react";
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { getAllPendingTransactions, getAllTransactionsByStatus, getPortefeuilleData, closeTransaction } from '../../Api/authApi.js.js';
import { formatPrice, parsePriceValue } from '../../functions/price-formater.js';
import { toast } from 'react-toastify';
import './portefeuille.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatSignedPrice(value) {
    const n = parsePriceValue(value);
    if (n === null) return '';
    const abs = formatPrice(Math.abs(n));
    if (!abs) return '';
    if (n > 0) return `+${abs}`;
    if (n < 0) return `-${abs}`;
    return abs;
}

// ── Palette par type de marché ────────────────────────────────────────────────

const TYPE_META = {
    'Actions':            { color: '#7AB8E0', label: 'Actions' },
    'Crypto-monnaies':    { color: '#E07AAB', label: 'Cryptos' },
    'Matieres-premieres': { color: '#E0C281', label: 'Matières' },
    'Devises':            { color: '#7AE0C5', label: 'Devises' },
};
const LIQUIDE_COLOR = '#5C6B88';

// ── Donut multi-segments ──────────────────────────────────────────────────────

function Donut({ segments, investiPct, size = 104 }) {
    const r = size / 2 - 8;
    const c = 2 * Math.PI * r;
    let offset = 0;
    return (
        <div className="pf-donut-wrap" style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
                <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="10"/>
                {segments.map((s, i) => {
                    const len = (s.pct / 100) * c;
                    const el = (
                        <circle key={i} cx={size/2} cy={size/2} r={r}
                            fill="none" stroke={s.color} strokeWidth="10"
                            strokeDasharray={`${len} ${c - len}`}
                            strokeDashoffset={-offset}
                            strokeLinecap="butt"/>
                    );
                    offset += len;
                    return el;
                })}
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
                <div>
                    <div className="num" style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>
                        {investiPct}<span style={{ fontSize: '0.4em', color: 'var(--text-dim)' }}> %</span>
                    </div>
                    <div style={{ color: 'var(--text-mute)', marginTop: -2 }} className="text">investi</div>
                </div>
            </div>
        </div>
    );
}

// ── Metric cell ───────────────────────────────────────────────────────────────

function Metric({ label, value, color }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ fontSize: 9.5, color: 'var(--text-mute)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {label}
            </span>
            <span className="num" style={{ fontSize: 13, fontWeight: 600, color: color || 'var(--text)' }}>{value}</span>
        </div>
    );
}

// ── Position card ─────────────────────────────────────────────────────────────

function PositionCard({ item, onModify, onClose, isFirst = false }) {
    const isLong = item.transaction_type === 'achat';
    const pnl = parseFloat(item.gain_perte_latent) || 0;
    const pnlUp = pnl >= 0;

    return (
        <div className="relief" style={{ borderRadius: 18, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                    width: 36, height: 36, borderRadius: 11, flexShrink: 0,
                    overflow: 'hidden',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid var(--border)',
                    display: 'grid', placeItems: 'center',
                }}>
                    <img src={item.market_icon} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }}/>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {item.market_name && (
                            <span style={{ fontSize: 14, fontWeight: 600 }}>{item.market_name}</span>
                        )}
                        <span style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                            background: isLong ? 'var(--up-soft)' : 'var(--down-soft)',
                            color: isLong ? 'var(--up)' : 'var(--down)',
                            letterSpacing: '0.04em', textTransform: 'uppercase',
                        }}>{isLong ? 'Long' : 'Short'}</span>
                        {parseInt(item.leverage) > 1 && (
                            <span style={{
                                fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                                background: 'var(--gold-soft)', color: 'var(--gold)',
                            }}>×{parseInt(item.leverage)}</span>
                        )}
                    </div>
                    <div className="num" style={{ fontSize: 11, color: 'var(--text-mute)', marginTop: 2 }}>
                        {parseFloat(item.quantity).toFixed(3)} · entrée {formatPrice(item.price)}
                    </div>
                </div>
            </div>

            <div id={isFirst ? 'pf-position-metrics' : undefined} style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                gap: 8,
                background: 'rgba(255,255,255,0.025)',
                border: '1px solid var(--border)',
                borderRadius: 12, padding: '10px 12px',
            }}>
                <Metric label="Investi" value={formatPrice(item.montant)}/>
                <Metric label="P&L" value={formatSignedPrice(item.gain_perte_latent)} color={pnlUp ? 'var(--up)' : 'var(--down)'}/>
                <Metric label="Valeur" value={formatPrice(item.valeur_actuelle)}/>
            </div>

            <div id={isFirst ? 'pf-position-actions' : undefined} style={{ display: 'flex', gap: 8 }}>
                <button onClick={onModify} className="pill-tap" style={{
                    flex: 1, height: 38, borderRadius: 10,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid var(--border)',
                    color: 'var(--text)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    touchAction: 'manipulation',
                }}>Modifier</button>
                <button onClick={onClose} className="pill-tap" style={{
                    flex: 1, height: 38, borderRadius: 10,
                    background: '#fff', border: 'none',
                    color: '#0A1226', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.9)',
                    touchAction: 'manipulation',
                }}><span className="mr-2">Fermer</span> {formatSignedPrice(item.gain_perte_latent)}</button>
            </div>
        </div>
    );
}

// ── History row ───────────────────────────────────────────────────────────────

function HistoryRow({ item }) {
    const isBuy = item.transaction_type === 'achat';
    const pnl = item.gain_loss != null ? parseFloat(item.gain_loss) : null;
    const date = item.created_at
        ? new Date(item.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
        : '';

    return (
        <div style={{
            display: 'grid', gridTemplateColumns: '32px 1fr auto', gap: 10, alignItems: 'center',
            padding: '10px 0',
        }}>
            <div style={{
                width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                overflow: 'hidden',
                background: isBuy ? 'var(--up-soft)' : 'var(--down-soft)',
                display: 'grid', placeItems: 'center',
            }}>
                <img src={item.market_icon} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }}/>
            </div>
            <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {isBuy ? 'Achat' : 'Vente'}
                    {item.market_name && (
                        <span style={{ color: 'var(--text-mute)', fontWeight: 500 }}> · {item.market_name}</span>
                    )}
                </div>
                <div className="num" style={{ fontSize: 11, color: 'var(--text-mute)', marginTop: 2 }}>
                    {parseFloat(item.quantity).toFixed(2)} · {date}
                </div>
            </div>
            <div style={{ textAlign: 'right' }}>
                <div className="num" style={{ fontSize: 13, fontWeight: 600 }}>{formatPrice(item.montant)}</div>
                {pnl != null && (
                    <div className="num" style={{
                        fontSize: 11, fontWeight: 600,
                        color: pnl >= 0 ? 'var(--up)' : 'var(--down)',
                    }}>{formatSignedPrice(pnl)}</div>
                )}
            </div>
        </div>
    );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const PorteFeuille = () => {
    const [view, setView] = useState('positions');
    const [portefeuilledata, setPortefeuilledata] = useState({});
    const [transactions, setTransactions] = useState([]);
    const [historyTransactions, setHistoryTransactions] = useState([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);

    const user = useSelector((state) => state.auth.user);
    const navigate = useNavigate();

    const fetchPositions = async () => {
        try {
            const data = await getAllPendingTransactions(user.id);
            setTransactions(data.transactions_pending || []);
        } catch (_) {}
    };

    const fetchPortefeuilleData = async () => {
        try {
            const data = await getPortefeuilleData(user.id);
            setPortefeuilledata(data);
        } catch (_) {}
    };

    const fetchHistory = async () => {
        try {
            const data = await getAllTransactionsByStatus(user.id);
            setHistoryTransactions(data.transactions || []);
            setHistoryLoaded(true);
        } catch (_) {}
    };

    useEffect(() => {
        fetchPositions();
        fetchPortefeuilleData();
        fetchHistory();
        const id = setInterval(fetchPositions, 4 * 60 * 1000);
        return () => clearInterval(id);
    }, [user]);

    const handleClose = async (uniqueId, currentValue) => {
        try {
            await closeTransaction(uniqueId, currentValue);
            toast.success('Position fermée');
            fetchPositions();
            fetchPortefeuilleData();
        } catch (_) {
            toast.error('Erreur lors de la fermeture');
        }
    };

    const isUp = !(portefeuilledata.difference_percentage < 0);
    const montantActuel = parseFloat(portefeuilledata.montant_actuel) || 0;
    const liquidite = parseFloat(portefeuilledata.liquidite) || 0;
    const totalInvesti = montantActuel - liquidite;
    const investiPct = montantActuel > 0 ? Math.round((totalInvesti / montantActuel) * 100) : 0;
    const liquiditePct = 100 - investiPct;

    // Répartition par type : somme des valeurs de position courantes (montant + gain/perte latent,
    // cohérent avec le calcul du capital total côté backend) par market_type.
    // On n'utilise pas `valeur_actuelle` : avec un effet de levier, c'est la valeur notionnelle
    // de la position (exposition) et non sa valeur réelle dans le portefeuille.
    const montantParType = transactions.reduce((acc, t) => {
        const type = t.market_type;
        if (!type) return acc;
        const valeurPosition = (parseFloat(t.montant) || 0) + (parseFloat(t.gain_perte_latent) || 0);
        acc[type] = (acc[type] || 0) + valeurPosition;
        return acc;
    }, {});

    // Segments pour le donut (types investis + liquidité)
    const typeSegments = Object.entries(TYPE_META)
        .filter(([type]) => montantParType[type] > 0)
        .map(([type, meta]) => ({
            color: meta.color,
            label: meta.label,
            pct: montantActuel > 0 ? Math.round((montantParType[type] / montantActuel) * 100) : 0,
        }));
    const donutSegments = [
        ...typeSegments,
        { color: LIQUIDE_COLOR, label: 'Liquidité', pct: liquiditePct },
    ];

    return (
        <div className="portefeuille-page w-full relative overflow-auto h-[100%]">

            {/* Heading */}
            <div style={{ marginTop: 8 }} className="xl:!my-8">
                <div id="pf-solde" style={{ padding: '12px 4px 14px' }}>
                    <div style={{ color: 'var(--text-mute)', fontSize: 13, fontWeight: 500 }}>Mon portefeuille</div>
                    <div className="num pf-capital-num" style={{ fontSize: 32, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.1, marginTop: 4 }}>
                        {montantActuel ? formatPrice(montantActuel) : '—'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                        {portefeuilledata.difference_percentage !== undefined && (
                            <span className="num" style={{
                                color: isUp ? 'var(--up)' : 'var(--down)',
                                background: isUp ? 'var(--up-soft)' : 'var(--down-soft)',
                                padding: '3px 8px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                            }}>
                                {isUp ? '+ ' : '- '}{formatPrice(Math.abs(parseFloat(portefeuilledata.difference || 0)))} <span className="mx-1.5"></span> {isUp ? '+ ' : '- '}{Math.abs(parseFloat(portefeuilledata.difference_percentage || 0)).toFixed(2)}%
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="xl:flex xl:gap-10 2xl:gap-12 items-start">
                {/* Répartition */}
                <div id="pf-repartition" className="relief pf-repartition xl:my-12 grow" style={{ borderRadius: 20, padding: 16, marginTop: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-mute)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            Répartition
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12 }}>
                            <Donut segments={donutSegments} investiPct={investiPct} size={104}/>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
                                {donutSegments.map(s => (
                                    <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }}/>
                                        <span style={{ fontSize: 12, fontWeight: 500, flex: 1 }}>{s.label}</span>
                                        <span className="num" style={{ fontSize: 12, color: 'var(--text-dim)' }}>{s.pct}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                </div>

                {/* Toggle + contenu */}
                <div className="pf-section" style={{ marginTop: 20 }}>

                    {/* Toggle */}
                    <div className="pf-section-toggle" style={{
                        display: 'flex', gap: 4,
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid var(--border)',
                        borderRadius: 12, padding: 4,
                    }}>
                        {[
                            { id: 'positions', label: 'Positions ouvertes' },
                            { id: 'history',   label: 'Historique' },
                        ].map(t => (
                            <button key={t.id} id={`pf-tab-${t.id}`} onClick={() => setView(t.id)} className="pill-tap" style={{
                                flex: 1, width: '50%', padding: '8px 0',
                                borderRadius: 9, border: 'none', cursor: 'pointer',
                                background: view === t.id ? 'rgba(255,255,255,0.08)' : 'transparent',
                                color: view === t.id ? 'var(--text)' : 'var(--text-dim)',
                                fontSize: 12, fontWeight: 600,
                                boxShadow: view === t.id ? 'inset 0 1px 0 rgba(255,255,255,0.06)' : 'none',
                                touchAction: 'manipulation',
                            }}>{t.label}</button>
                        ))}
                    </div>

                    {/* Positions */}
                    {view === 'positions' && (
                        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                            {transactions.length === 0 ? (
                                <p style={{ color: 'var(--text-mute)', fontSize: 13, textAlign: 'center', padding: '24px 0', flex: '1 1 100%' }}>
                                    Aucune position ouverte.
                                </p>
                            ) : (
                                transactions.map((item, index) => (
                                    <div key={item.unique_id} className="portefeuille-position-card">
                                        <PositionCard
                                            item={item}
                                            isFirst={index === 0}
                                            onModify={() => navigate(`/portefeuille_menu/${item.market_id}/${item.league_id}`)}
                                            onClose={() => handleClose(item.unique_id, item.current_value)}
                                        />
                                    </div>
                                ))
                            )}
                            <div style={{ flex: '1 1 100%' }}>
                                <button onClick={() => navigate('/portefeuille_menu')} className="button-new-trade pill-tap" style={{
                                    width: '100%', height: 50, borderRadius: 14, marginTop: 4,
                                    background: 'rgba(255,255,255,0.04)',
                                    border: '1px dashed var(--border-strong)',
                                    color: 'var(--text-dim)',
                                    cursor: 'pointer',
                                    fontSize: 13, fontWeight: 600,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                    touchAction: 'manipulation',
                                }}>
                                    <svg width="14" height="14" viewBox="0 0 14 14">
                                        <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                                    </svg>
                                    Ouvrir un nouveau trade
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Historique */}
                    {view === 'history' && (
                        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                            {historyTransactions.length === 0 ? (
                                <p style={{ color: 'var(--text-mute)', fontSize: 13, textAlign: 'center', padding: '24px 0', flex: '1 1 100%' }}>
                                    Aucun historique disponible.
                                </p>
                            ) : (
                                historyTransactions.map((item, i) => (
                                    <div key={item.unique_id || i} className="relief" style={{ flex: '1 1 340px', maxWidth: 450, borderRadius: 14, padding: '4px 14px' }}>
                                        <HistoryRow item={item}/>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default PorteFeuille;
